import { addMessageQueue, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import Message from '@/services/message'
import Loggers from '@/services/logger'

/**
 * ファイルをダウンロードさせる
 * @param fileData data:application/pdf;base64,JVB.....
 * @param fileName ダウンロードさせるファイル名
 * @param type application/pdfなど
 */
export const download = (fileData: string, fileName: string, type: string): void => {
  if (window.navigator.msSaveOrOpenBlob) {
    // for IE
    let b = null
    const bin = atob(fileData)
    const buffer = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i)
    }

    try {
      b = new Blob([buffer.buffer], {
        type: type,
      })
    } catch (e) {
      if (e.name == 'InvalidStateError') {
        // windows10未満のIE11の場合
        const msb = new MSBlobBuilder()
        msb.append(buffer.buffer)
        b = msb.getBlob(type)
      } else {
        // ここに入る場合、完全にBlobに対応していないバージョンとなる
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NOT_APPLICABLE_BROWSER)
        Loggers.warn('tried to download with a browser that is not the target of operation.')
        return
      }
    }
    window.navigator.msSaveOrOpenBlob(b, fileName)
  } else {
    // その他ブラウザ
    const linkEl = document.createElement('a')
    linkEl.href = fileData
    linkEl.setAttribute('download', fileName)
    document.body.appendChild(linkEl)
    linkEl.click()
    linkEl.remove()
  }
}

/**
 * ファイル名から拡張子を取得
 * @param fileName ファイル名
 * @returns
 */
export const getExt = (fileName: string): string | null => {
  const split = fileName.split('.')
  if (split.length < 2) {
    // 拡張子が無い場合、nullを返す
    return null
  }
  return split.pop() ?? null
}

/**
 * ファイル名から許可された拡張子かどうかを判定する
 * @param fileName ファイル名
 * @param allowExts 許可する拡張子配列 .pdf等
 * @returns
 */
export const isAllowExt = (fileName: string, allowExts: string[]): boolean => {
  const ext = getExt(fileName)
  if (ext == null) {
    // 拡張子が付与されていない場合false
    return false
  }
  if (allowExts.indexOf('.*') >= 0) {
    // 全て許可ならtrue
    return true
  }
  const extLowerCase = ext.toLowerCase()
  return allowExts.indexOf('.' + extLowerCase) >= 0
}

/**
 * base64エンコードされたURI形式のデータからMimeTypeを抜き出す
 * @param fileData data:application/pdf;base64,ZGF0YTphc....
 * @returns
 */
export const getMimeTypeFromBase64 = (fileData: string): string => {
  const split = fileData.split(',')
  if (split.length < 2) {
    // 不正データはerror
    throw new Error('base64 uri format error')
  }
  const header = split[0]
  // dataとbase64を削除
  return header.replace('data:', '').replace(';base64', '')
}

/**
 * MB単位をbyteに変換する
 * @param mb
 * @returns
 */
export const mbToByte = (mb: number): number => {
  return mb * 1024 * 1024
}

/**
 * byte単位をMBに変換する
 * @param byte
 * @param round 四捨五入するか
 * @returns
 */
export const byteToMb = (byte: number, round = false): number => {
  const mb = byte / 1024 / 1024
  if (round) {
    return Math.round(mb)
  } else {
    return mb
  }
}

/**
 * base64の文字列を生成
 * @param mimeType string
 * @param file string
 * @returns string
 */
export const createBase64Str = (mimeType: string, file: string): string => {
  return `data:${mimeType};base64,${file}`
}

/**
 * 拡張子含むファイル名が指定の文字数を超えていないか判定する
 * @param fileName ファイル名
 * @param fileNameMaxLength ファイル名の最大長
 * @returns
 */
export const isOverFileNameMaxLength = (fileName: string, fileNameMaxLength: number): boolean => {
  const fileNameLength = Array.from(fileName).length
  if (fileNameLength <= fileNameMaxLength) {
    return false
  } else {
    return true
  }
}

/**
 * ファイルをダウンロードして、base64をでコードする
 * @param data ファイル
 * @param fileName ファイル名
 * @returns
 */
export const fileDownload = (data: { mime_type: string; file: string }, fileName: string): void => {
  // ダウンロード用情報
  const base64: string = createBase64Str(data.mime_type as string, data.file as string)
  download(base64, fileName, data.mime_type as string)
}
